import { useFilter } from '@/hooks/filter';
import { DateFormatType } from '@/modules/core/domain/interfaces';
import { Box, Button, Input, MenuDivider } from '@chakra-ui/react';
import { FilterType } from '@queryfilter/domain/interfaces';
import _ from 'lodash';
import React, { useCallback, useEffect, useState } from 'react';
import ReactFocusLock from 'react-focus-lock';
import { FiArrowLeft } from 'react-icons/fi';

import {
  ChakraCheckboxFilter,
  ChakraRadioFilter,
  ChakraRangeDateFilter,
} from './types';
import ChakraCheckboxFilterView from './types/ChakraCheckboxFilter/View';
import ChakraRadioFilterView from './types/ChakraRadioFilter/View';
import ChakraRangeDateFilterView from './types/ChakraRangeDateFilter/View';

const FilterItem: React.FC<any> = (props) => {
  const [search, setSearch] = useState<string>('');
  const [isFocusLockEnabled, setIsFocusLockEnabled] = useState(false);
  const { getOptions, selectOption, selectedOptions } = useFilter();
  const [currentSelected, setCurrentSelected] = useState<string[]>([]);
  const [changePageOn, setChangePageOn] = useState<boolean>(false);
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [hasNextPage, setHasNextPage] = useState<boolean>(true);
  const {
    column,
    title,
    type,
    dateFormat,
    isMobile,
    initialFilter,
    onClose,
    isModeView,
    onClearFilter,
    maxSelection,
  } = props;
  const [options, setOptions] = useState<any>([]);

  const handleInputClick = () => {
    if (!isFocusLockEnabled) {
      setIsFocusLockEnabled(true);
    }
  };

  const [selected, setSelected] = useState(() => {
    if (selectedOptions[column]) {
      return selectedOptions[column].selected;
    }
    return null;
  });

  useEffect(() => {
    if (selectedOptions[column]) {
      setSelected(selectedOptions[column].selected);
    } else {
      setSelected(null);
    }
  }, [selectedOptions, column]);

  const onSelect = useCallback(
    (value: string[]) => {
      selectOption({ selected: value, column, type });
      setCurrentSelected(value);
    },
    [selectOption, column, type],
  );

  const onPaginate = useCallback(
    async (page: number) => {
      setChangePageOn(true);
      const options = await getOptions(column, page, search);
      setHasNextPage(options?.hasNext);
      setOptions((prevOptions: any) => [...prevOptions, ...options?.options]);
      setChangePageOn(false);
    },
    [search],
  );

  const onOpen = useCallback(
    async (page: number = 1, searchValue?: string) => {
      const options = await getOptions(column, page, searchValue, title);

      setOptions(options?.options);
      setHasNextPage(options?.hasNext);
      return options;
    },
    [column, getOptions, title],
  );

  useEffect(() => {
    const typeToOptions =
      type === FilterType.DATE || type === FilterType.DATE_RANGE;
    if (options.length === 0 && !typeToOptions) {
      onOpen();
    }
  }, []);

  const onSearch = useCallback(
    _.debounce(async (currentSearchValue: string) => {
      const selectedOptions =
        Array.isArray(currentSelected) && currentSelected?.length > 0
          ? currentSelected?.map((option) => ({
              key: option,
              value: option,
            }))
          : [];

      const options = await getOptions(column, 1, currentSearchValue);

      setOptions(_.unionBy(selectedOptions, options?.options, 'key'));
      setCurrentPage(1);
      setHasNextPage(options?.hasNext);
    }, 800),
    [column, getOptions, currentSelected],
  );

  const SearchBar = ({ type }: { type: FilterType }) => {
    const disableSearch =
      type === FilterType.DATE || type === FilterType.DATE_RANGE;

    return (
      <>
        <Box
          display="flex"
          alignContent="center"
          alignItems="center"
          padding="0"
        >
          <Button
            leftIcon={
              <FiArrowLeft size={14} style={{ backgroundColor: 'none' }} />
            }
            bgColor="transparent"
            paddingEnd="0"
            onClick={() => {
              setSearch(() => '');
              onClose(false);
            }}
          />
          <ReactFocusLock
            returnFocus
            persistentFocus
            disabled={!isFocusLockEnabled || disableSearch}
          >
            <Input
              variant="outline"
              placeholder="Filter"
              border="none"
              role="search"
              value={search}
              paddingStart="0"
              focusBorderColor="none"
              disabled={disableSearch}
              onChange={(e) => {
                setSearch(e.target.value);
                onSearch(e.target.value);
              }}
              onClick={handleInputClick}
            />
          </ReactFocusLock>
        </Box>
        <MenuDivider />
      </>
    );
  };

  switch (type) {
    case FilterType.SINGLE:
      return isModeView ? (
        <ChakraRadioFilterView
          title={title}
          key={`${title}-${type}`}
          selected={selected}
          options={options}
          onClear={() => onClearFilter(column)}
        />
      ) : (
        <>
          <SearchBar type={FilterType.MULTI} />
          <ChakraRadioFilter
            title={title}
            selected={selected}
            options={options}
            onPaginate={onPaginate}
            setChangePageOn={setChangePageOn}
            changePageOn={changePageOn}
            setCurrentPage={setCurrentPage}
            hasNextPage={hasNextPage}
            currentPage={currentPage}
            onOpen={onOpen}
            onSelect={onSelect}
            initialSelected={selected ?? initialFilter.option}
          />
        </>
      );
    case FilterType.MULTI:
      return isModeView ? (
        <ChakraCheckboxFilterView
          title={title}
          key={`${title}-${type}`}
          selected={selected}
          options={options}
          onClear={() => onClearFilter(column)}
        />
      ) : (
        <>
          <SearchBar type={FilterType.MULTI} />
          <ChakraCheckboxFilter
            selected={selected}
            options={options}
            onPaginate={onPaginate}
            setChangePageOn={setChangePageOn}
            changePageOn={changePageOn}
            setCurrentPage={setCurrentPage}
            hasNextPage={hasNextPage}
            currentPage={currentPage}
            onOpen={onOpen}
            onSelect={onSelect}
            initialSelected={selected}
            isMobile={isMobile}
            maxSelection={maxSelection}
          />
        </>
      );
    case FilterType.DATE_RANGE:
      return isModeView ? (
        <ChakraRangeDateFilterView
          title={title}
          key={`${title}-${type}`}
          selected={selected}
          onClear={() => onClearFilter(column)}
          initialSelected={selected ?? initialFilter.option}
        />
      ) : (
        <>
          <SearchBar type={FilterType.DATE_RANGE} />
          <ChakraRangeDateFilter
            isRange
            onSelect={onSelect}
            selected={selected}
            title={title}
            isMobile={isMobile}
            initialSelected={selected ?? initialFilter.option}
            dateFormat={dateFormat || DateFormatType.UNIX_TIMESTAMP}
          />
        </>
      );
    case FilterType.DATE:
      return isModeView ? (
        <ChakraRangeDateFilterView
          title={title}
          key={`${title}-${type}`}
          selected={selected}
          onClear={() => onClearFilter(column)}
          initialSelected={selected ?? initialFilter.option}
        />
      ) : (
        <>
          <SearchBar type={FilterType.DATE} />
          <ChakraRangeDateFilter
            onSelect={onSelect}
            selected={selected}
            title={title}
            isMobile={isMobile}
            initialSelected={initialFilter.option}
            dateFormat={dateFormat || DateFormatType.UNIX_TIMESTAMP}
          />
        </>
      );
    default:
      return null;
  }
};

export default FilterItem;
